import { memo, useMemo } from 'react'
import {
  Box,
  Typography,
  Grid,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material'

import Icon from '../../../../../../components/icon'
import Chip from '../../../../../../components/chip'
import RadarChart from '../../../../../../components/charts/radar'

import {
  PSYCHOMETRIC_COLORS,
  PSYCHOMETRIC_STATUS,
  PSYCHOMETRIC_TRAIT_LABELS
} from '../../../../../../utils/constant'
import { hexToRGB } from '../../../../../../utils/utils'

export const PsychometricResultUnavailable = memo(
  ({ attemptedQuestions, totalQuestions }) => (
    <Box
      sx={{
        p: 2,
        backgroundColor: 'var(--background-paper)',
        borderRadius: '8px',
        border: '1px solid var(--divider)',
        borderLeft: '4px solid var(--warning-main)',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Icon
        icon="mdi:alert-outline"
        style={{
          fontSize: '24px',
          color: 'var(--warning-main)',
          flexShrink: 0
        }}
      />
      <Box>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            color: 'var(--text-primary)',
            mb: 0.25
          }}
        >
          Result Unavailable
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
          The candidate attempted <b>{attemptedQuestions}</b> out of{' '}
          <b>{totalQuestions}</b> questions, which is not sufficient to generate
          the result.
        </Typography>
      </Box>
    </Box>
  )
)

export const PsychometricTraitCard = memo(
  ({ forPdf = false, trait, dominantStyle, secondaryStyle }) => (
    <Box
      sx={{
        border: '1px solid var(--divider)',
        borderRadius: '12px',
        padding: '20px',
        height: forPdf ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        pageBreakInside: 'avoid'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 700
                // textTransform: 'capitalize'
              }}
            >
              {trait?.name}
            </Typography>
            {dominantStyle?.toLowerCase() === trait?.name?.toLowerCase() && (
              <Chip
                label="Primary"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '11px',
                  fontWeight: 600,
                  color: trait?.color,
                  bgcolor: `${trait?.color}15`
                }}
              />
            )}
            {secondaryStyle?.toLowerCase() === trait?.name?.toLowerCase() && (
              <Chip
                label="Secondary"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '11px',
                  fontWeight: 600,
                  color: trait?.color,
                  bgcolor: `${trait?.color}15`
                }}
              />
            )}
          </Box>
          <Typography
            sx={{
              fontSize: '13px',
              color: trait?.color,
              fontWeight: 600
            }}
          >
            {trait?.subTitle}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '28px',
            fontWeight: 800,
            color: trait?.color,
            lineHeight: 1
          }}
        >
          {trait?.score}
          <Typography
            component="span"
            sx={{
              fontSize: '12px',
              color: 'var(--text-secondary)'
            }}
          >
            {' '}
            /100
          </Typography>
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={trait?.score}
        sx={{
          height: '8px',
          borderRadius: '4px',
          backgroundColor: 'var(--action-hover)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: trait?.color,
            borderRadius: '4px'
          }
        }}
      />

      <Typography
        sx={{
          fontSize: '13px',
          lineHeight: 1.6,
          color: 'var(--text-secondary)',
          mt: 'auto'
        }}
      >
        {trait?.description}
      </Typography>
    </Box>
  )
)

export const PsychometricTestLayout = memo(
  ({ testData, forPdf, isSideBySide = false }) => {
    const traitEntries = useMemo(() => {
      return Object.entries(testData?.traits || {}).map(
        ([key, score], index) => {
          const level = testData?.traitLevels?.[key]
          return {
            key,
            name: PSYCHOMETRIC_TRAIT_LABELS[key] || key,
            score: score ?? 0,
            subTitle: level?.title || '',
            description: level?.description || '',
            color: PSYCHOMETRIC_COLORS[index]
          }
        }
      )
    }, [testData?.traits, testData?.traitLevels])

    const radarIndicators = useMemo(
      () => traitEntries?.map((t) => t?.name),
      [traitEntries]
    )
    const radarValues = useMemo(
      () => traitEntries?.map((t) => t?.score),
      [traitEntries]
    )

    const dominantStyle = useMemo(
      () =>
        testData?.primaryDisc
          ? PSYCHOMETRIC_TRAIT_LABELS[testData?.primaryDisc] ||
            testData?.primaryDisc
          : null,
      [testData?.primaryDisc]
    )

    const secondaryStyle = useMemo(
      () =>
        testData?.secondaryDisc
          ? PSYCHOMETRIC_TRAIT_LABELS[testData?.secondaryDisc] ||
            testData?.secondaryDisc
          : null,
      [testData?.secondaryDisc]
    )

    const archetypeName = useMemo(
      () => testData?.archeType?.archetype || '',
      [testData?.archeType?.archetype]
    )
    const archetypeDescription = useMemo(
      () => testData?.archeType?.summary || '',
      [testData?.archeType?.summary]
    )
    const strength = useMemo(
      () => testData?.archeType?.strength || '',
      [testData?.archeType?.strength]
    )
    const suggestedRoles = useMemo(
      () => testData?.archeType?.suggestedRoles || [],
      [testData?.archeType?.suggestedRoles]
    )

    return (
      <>
        <Grid container sx={{ mb: 4, alignItems: 'center' }}>
          <Grid item xs={12} md={isSideBySide ? 6 : 12}>
            {/* ArcheType + Description */}
            {archetypeName && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: hexToRGB('#3498db'),
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '22px'
                  }}
                >
                  {archetypeName}
                </Typography>
                <Typography
                  sx={{
                    color: 'var(--text-secondary)',
                    lineHeight: 1.6,
                    fontSize: '16px'
                  }}
                >
                  {archetypeDescription}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={isSideBySide ? 6 : 12}>
            {/* Radar Chart - Centered */}
            <Box
              sx={{
                width: '100%',
                maxWidth: 550,
                mx: 'auto',
                pageBreakInside: 'avoid'
              }}
            >
              <Box sx={{ height: isSideBySide ? 270 : 320 }}>
                <RadarChart
                  indicators={radarIndicators}
                  values={radarValues}
                  max={100}
                  showFullLengthTitle
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {traitEntries?.map((trait) => (
            <Grid item xs={12} md={6} key={trait?.key}>
              <PsychometricTraitCard
                trait={trait}
                forPdf={forPdf}
                dominantStyle={dominantStyle}
                secondaryStyle={secondaryStyle}
              />
            </Grid>
          ))}
        </Grid>

        {/* Footer Info (Strengths & Roles) */}
        {(strength || suggestedRoles?.length > 0) && (
          <Box
            sx={{
              border: '1px solid var(--divider)',
              borderRadius: '12px',
              p: '20px',
              pageBreakInside: 'avoid'
            }}
          >
            <Grid container spacing={4}>
              {strength && (
                <Grid item xs={12} md={suggestedRoles?.length > 0 ? 6 : 12}>
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 700,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Icon
                      icon="mdi:star-outline"
                      color="var(--primary-main)"
                      fontSize={20}
                    />
                    Key Strengths
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5
                    }}
                  >
                    {strength
                      ?.split('. ')
                      ?.filter((s) => s?.trim())
                      .map((sentence, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5
                          }}
                        >
                          <Icon
                            icon="mdi:check-circle-outline"
                            color="var(--success-main)"
                            fontSize={18}
                            style={{ marginTop: '2px', flexShrink: 0 }}
                          />
                          <Typography
                            sx={{
                              fontSize: '13.5px',
                              color: 'var(--text-secondary)'
                            }}
                          >
                            {sentence?.endsWith('.')
                              ? sentence
                              : `${sentence}.`}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                </Grid>
              )}
              {suggestedRoles?.length > 0 && (
                <Grid item xs={12} md={strength ? 6 : 12}>
                  <Typography
                    sx={{
                      fontSize: '15px',
                      fontWeight: 700,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Icon
                      icon="mdi:briefcase-outline"
                      color="var(--primary-main)"
                      fontSize={20}
                    />
                    Suggested Roles
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {suggestedRoles?.map((role, idx) => (
                      <Chip
                        key={idx}
                        label={role}
                        sx={{
                          backgroundColor: 'var(--action-hover)',
                          border: '1px solid var(--divider)',
                          borderRadius: '8px',
                          fontWeight: 500,
                          fontSize: '13px',
                          px: 1
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </>
    )
  }
)

export const PsychometricReport = memo(({ testData, forPdf }) => {
  const title = useMemo(
    () => testData?.title || `Personality Analysis`,
    [testData]
  )
  if (!testData) return null

  return (
    <Accordion
      defaultExpanded
      sx={{
        borderRadius: '12px !important',
        overflow: 'hidden',
        border: '1px solid var(--divider)',
        boxShadow: 'none !important',
        '&:before': { display: 'none' },
        margin: '0px'
      }}
    >
      <AccordionSummary
        expandIcon={<Icon icon="mdi:chevron-down" />}
        sx={{
          minHeight: '64px',
          py: 2,
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            pr: 2
          }
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: 'var(--text-primary)' }}
        >
          {title}
        </Typography>
        {testData?.totalQuestions != null && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'var(--grey-100)',
              px: 1.5,
              py: 0.5,
              borderRadius: '6px'
            }}
          >
            <Typography
              fontSize="12px"
              color="var(--grey-600)"
              fontWeight={600}
            >
              Attempted:{' '}
              <Typography
                component="span"
                fontSize="12px"
                fontWeight={800}
                color="var(--grey-800)"
              >
                {testData.attemptedQuestions || 0}/{testData.totalQuestions}
              </Typography>{' '}
              Questions
            </Typography>
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 4, pt: 2 }}>
        {testData?.status === PSYCHOMETRIC_STATUS.INSUFFICIENT_DATA ? (
          <PsychometricResultUnavailable
            attemptedQuestions={testData?.attemptedQuestions}
            totalQuestions={testData?.totalQuestions}
          />
        ) : (
          <PsychometricTestLayout
            testData={testData}
            forPdf={forPdf}
            title={title}
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
})
